import "./index.scss";
import React from "react";

import {
  Avatar,
  OverseasMark,
  RolePill,
  StatusPill,
  money,
  shortName,
} from "../atoms";
import { PHONE, useMediaQuery } from "../../hooks/useMediaQuery";

// The four buckets a block keeps its players in, flattened in the order the
// room works through them: whoever is up now, then who is still to come, then
// the results.
const flatten = (players) => [
  ...(players?.startedPlayers || []),
  ...(players?.players || []),
  ...(players?.soldPlayers || []),
  ...(players?.unsoldPlayers || []),
];

// ---------------------------------------------------------------------------
// Phone: one card per player
// ---------------------------------------------------------------------------
// Six columns of nowrap text do not fit in the ~350px a phone leaves inside the
// panel, and a grid that scrolls sideways inside a rounded surface reads as
// broken rather than as scrollable. The same facts stack instead, and the sale
// line is dropped outright when there is no sale - where the grid had to print
// a dash to hold its column open.
const PlayerCards = ({ data }) => (
  <ul className="au-cards">
    {data.map((player, i) => (
      <li key={player?._id || i} className="au-card-row">
        <Avatar name={player?.name} src={player?.image} size={34} />

        <div className="au-card-body">
          <span className="au-card-name">
            {shortName(player?.name)}
            <OverseasMark nationality={player?.nationality} />
          </span>

          <span className="au-card-meta">
            <RolePill role={player?.role} />
            <span className="au-card-fact">
              <span className="au-card-key">Base</span>
              {money(player?.basePrice)}
            </span>
          </span>

          {player?.isSold && (
            <span className="au-card-sale">
              Sold for <b>{money(player?.soldAt)}</b>
              {player?.lastBidBy?.team ? (
                <>
                  {" "}
                  to <b>{player.lastBidBy.team}</b>
                </>
              ) : null}
            </span>
          )}
        </div>

        <StatusPill player={player} />
      </li>
    ))}
  </ul>
);

// ---------------------------------------------------------------------------
// Everything wider: the grid
// ---------------------------------------------------------------------------
const PlayerRows = ({ data }) => (
  <table className="au-table">
    <thead>
      <tr>
        <th>Player</th>
        <th>Role</th>
        <th className="num">Base</th>
        <th className="num">Sold at</th>
        <th>Sold to</th>
        <th className="end">Status</th>
      </tr>
    </thead>
    <tbody>
      {data.map((player, i) => (
        <tr key={player?._id || i}>
          <td className="who">
            <Avatar name={player?.name} src={player?.image} />
            <span className="name">
              {shortName(player?.name)}
              <OverseasMark nationality={player?.nationality} />
            </span>
          </td>
          <td>
            <RolePill role={player?.role} />
          </td>
          <td className="num">{money(player?.basePrice)}</td>
          <td className="num strong">
            {player?.isSold ? money(player?.soldAt) : "—"}
          </td>
          <td>{player?.isSold ? player?.lastBidBy?.team || "—" : "—"}</td>
          <td className="end">
            <StatusPill player={player} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ViewPlayersTable = ({ players }) => {
  const data = flatten(players);
  const phone = useMediaQuery(PHONE);

  if (!data.length) {
    return (
      <div className="au-table-wrap">
        <p className="au-empty">No players in this category yet.</p>
      </div>
    );
  }

  return (
    <div className="au-table-wrap">
      {phone ? <PlayerCards data={data} /> : <PlayerRows data={data} />}
    </div>
  );
};

export default ViewPlayersTable;
